/**
 * Import a provider from a bundle on the operator's workstation (REQ-UI-157;
 * REQ-PROVIDER-023; Flow V §5c).
 *
 * # Review, then install — two acts, not one
 *
 * The operator picks a file; this dialog reads it **locally** and shows what the
 * archive contains — the provider, its interfaces, each interface's trust mode,
 * transport security and conversion mode, its connections, and every artifact
 * with the digest the installer will verify it against. Nothing is sent until
 * they confirm. Reading a bundle a partner site handed you must not be the same
 * act as installing it, and the BFF's import route has no dry-run: it unpacks
 * and installs. So the preview is a local parse
 * (:file:`provider-bundle-preview.ts`), which also means it costs no request.
 *
 * # The install button is visible and disabled, and that is deliberate
 *
 * REQ-UI-143's absent-not-disabled rule governs **permissions**. This is the
 * other case: a capability the operator *has*, which the surface cannot perform
 * yet. Hiding it would conceal a gap in the bundle rather than a privilege the
 * session lacks — so it stays, disabled, with the reason stated.
 *
 * # The outcome is reported, not collapsed
 *
 * This import is the one deliberate path that can re-apply a bundle
 * (REQ-PROVIDER-012 as amended 2026-10-07; the startup pass never does). So it
 * has four outcomes, and the dialog keeps them apart: **installed** (new, or a
 * provider this deployment deleted and the bundle re-created), **replaced** (the
 * provider existed and its configuration is now the bundle's), **already
 * applied** (same bundle, provider still here — nothing changed, truthfully),
 * and **refused** (with the reason). A bare success would make the first three
 * look alike, and an operator who just overwrote a live intake must be told so.
 *
 * Cross-references:
 *   - REQ-UI-157; REQ-PROVIDER-023; ADR-040 §D-3 (seed-then-own).
 *   - `provider-bundle-preview.ts` (the local parse); `use-providers.ts` (the POST).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useRef, useState } from "react";
import {
  BundleUnreadable,
  isProviderBundleInstallable,
  parseProviderBundle,
  type ProviderBundlePreview,
} from "@/providers/provider-bundle-preview";
import { findTrustMode } from "@/providers/provider-catalog";
import { useImportProviderBundle, type ProviderImportOutcome } from "@/providers/use-providers";
import { cn } from "@/lib/utils";

/** Props for {@link ProviderImportDialog}. */
export interface ProviderImportDialogProps {
  onClose: () => void;
  /** Called with the installed provider's name once the BFF reports it applied. */
  onInstalled: (providerName: string) => void;
}

/** Render the provider import review dialog. */
export function ProviderImportDialog({
  onClose,
  onInstalled,
}: ProviderImportDialogProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ProviderBundlePreview | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ProviderImportOutcome | null>(null);
  const importBundle = useImportProviderBundle();

  const choose = async (chosen: File | undefined): Promise<void> => {
    if (chosen === undefined) return;
    setFile(chosen);
    setPreview(null);
    setOutcome(null);
    setReadError(null);
    try {
      setPreview(await parseProviderBundle(await chosen.arrayBuffer()));
    } catch (caught) {
      // The parser's messages are written FOR an operator, so they surface
      // verbatim. Anything else is generic (REQ-UI-037 — no internal leakage).
      setReadError(
        caught instanceof BundleUnreadable ? caught.message : "This file could not be read.",
      );
    }
  };

  const installable = preview !== null && isProviderBundleInstallable(preview);

  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/60" />
        <Dialog.Content
          data-testid="provider-import-dialog"
          aria-describedby={undefined}
          // `system-status-scope` travels with the content because Radix portals
          // to <body> — see the management dialog for the full reasoning.
          className={cn(
            "system-status-scope fixed left-1/2 top-1/2 z-[60] flex max-h-[82vh] w-[760px]",
            "max-w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-md",
            "border border-border bg-bg-surface shadow-lg",
          )}
        >
          <header className="flex items-center gap-3 border-b border-border px-4 py-2">
            <Dialog.Title className="text-body font-medium text-text-primary">
              Import provider
            </Dialog.Title>
            <span className="text-meta text-text-secondary">Review before installing</span>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close import"
                data-testid="provider-import-close"
                className="ml-auto rounded p-1 text-text-secondary hover:bg-bg-elevated"
              >
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
            <input
              ref={inputRef}
              type="file"
              accept=".gz,.tgz,application/gzip"
              className="hidden"
              data-testid="provider-import-file"
              onChange={(event) => void choose(event.target.files?.[0])}
            />
            <div className="flex items-center gap-2 pb-3">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid="provider-import-choose"
                className="rounded border border-border px-2 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
              >
                Choose bundle…
              </button>
              <span className="text-meta text-text-muted" data-testid="provider-import-filename">
                {file === null ? "No file selected." : file.name}
              </span>
            </div>

            {readError !== null && (
              <p
                role="alert"
                data-testid="provider-import-unreadable"
                className="text-body-sm text-destructive"
              >
                {readError}
              </p>
            )}

            {preview !== null && outcome === null && (
              <PreviewBody preview={preview} installable={installable} />
            )}

            {outcome !== null && <OutcomeBody outcome={outcome} />}
          </div>

          <footer className="flex items-center gap-2 border-t border-border px-4 py-2">
            <span className="text-meta text-text-muted" data-testid="provider-import-footnote">
              {outcome !== null
                ? "The installer's report is above."
                : preview === null
                  ? "Nothing is sent until you choose a bundle and confirm."
                  : installable
                    ? "Installs one provider, its interfaces and its connections — replacing them if this deployment already has it — and records the act."
                    : "Install is unavailable until the problems above are resolved."}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                data-testid="provider-import-cancel"
                className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
              >
                {outcome === null ? "Cancel" : "Close"}
              </button>
              {outcome === null && (
                <button
                  type="button"
                  // Visible and disabled — see the module docstring. This is the
                  // capability-gap case, not the permission case.
                  disabled={!installable || file === null || importBundle.isPending}
                  data-testid="provider-import-confirm"
                  onClick={() => {
                    if (file === null) return;
                    importBundle.mutate(file, {
                      onSuccess: (result) => {
                        setOutcome(result);
                        if (result.applied.length > 0 && result.provider !== null) {
                          onInstalled(result.provider.name);
                        }
                      },
                    });
                  }}
                  className="rounded bg-brand px-3 py-1 text-body-sm font-medium text-bg-base hover:bg-brand-dim disabled:opacity-50"
                >
                  {importBundle.isPending ? "Installing…" : "Install provider"}
                </button>
              )}
            </div>
          </footer>

          {importBundle.isError && (
            <p
              role="alert"
              data-testid="provider-import-error"
              className="px-4 pb-2 text-meta text-destructive"
            >
              {describeImportError(importBundle.error.status, importBundle.error.body)}
            </p>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** What the archive contains, shown before anything is written. */
function PreviewBody({
  preview,
  installable,
}: {
  preview: ProviderBundlePreview;
  installable: boolean;
}): React.ReactElement {
  const unresolved = preview.placeholders.filter((p) => p.unresolved);
  const tunnelInterfaces = preview.interfaces.filter((i) => i.trustMode === "tunnel");
  return (
    <div className="flex flex-col gap-3" data-testid="provider-import-preview">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm">
        <dt className="text-text-secondary">Bundle</dt>
        <dd className="font-mono text-text-primary">{preview.bundleName}</dd>
        <dt className="text-text-secondary">Provider</dt>
        <dd className="font-mono text-text-primary">{preview.providerName}</dd>
        {preview.providerDescription !== null && (
          <>
            <dt className="text-text-secondary">Description</dt>
            <dd className="text-text-primary">{preview.providerDescription}</dd>
          </>
        )}
      </dl>

      {unresolved.length > 0 && (
        <div
          role="alert"
          data-testid="provider-import-blocked"
          className="rounded border border-destructive px-3 py-2 text-meta text-destructive"
        >
          <p className="pb-1">
            <strong>
              {unresolved.length} unresolved placeholder{unresolved.length === 1 ? "" : "s"}.
            </strong>{" "}
            This bundle ships with values the receiving site fills in, and{" "}
            {unresolved.length === 1 ? "one has" : "these have"} not been. Installing now would
            store the placeholder text itself as configuration, and the interface would fail to
            start later with nothing pointing back here.
          </p>
          <ul className="flex flex-col gap-0.5">
            {unresolved.map((placeholder) => (
              <li key={placeholder.token} className="font-mono">
                ${"{"}
                {placeholder.token}
                {"}"}
                {placeholder.prompt !== "" && (
                  <span className="font-sans"> — {placeholder.prompt}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview.digestProblems.length > 0 && (
        <div
          role="alert"
          data-testid="provider-import-digest-problem"
          className="rounded border border-destructive px-3 py-2 text-meta text-destructive"
        >
          <p className="pb-1">
            <strong>This archive does not match its own manifest.</strong> It is not the bundle the
            manifest describes, which the deployment will refuse as well.
          </p>
          <ul className="flex flex-col gap-0.5">
            {preview.digestProblems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Surfaced ahead of the interface list rather than only on each row: the
          single most consequential fact in a provider bundle is that installing
          it may admit a source Meridian will not authenticate (ADR-023), and an
          operator scanning a long list should not have to find it. */}
      {tunnelInterfaces.length > 0 && (
        <p
          role="status"
          data-testid="provider-import-tunnel-notice"
          className="rounded border border-warning px-3 py-2 text-meta text-warning"
        >
          {tunnelInterfaces.length === 1
            ? "One interface in this bundle runs in tunnel trust mode. "
            : `${tunnelInterfaces.length} interfaces in this bundle run in tunnel trust mode. `}
          {findTrustMode("tunnel")?.delegation}
        </p>
      )}

      {installable && (
        <p className="text-meta text-text-muted" data-testid="provider-import-ready">
          Every placeholder in this bundle is filled in and every member matches its digest. Nothing
          has been written yet.
        </p>
      )}

      <section>
        <h3 className="pb-1 text-body-sm font-medium text-text-primary">
          Interfaces and connections
        </h3>
        {preview.interfaces.length === 0 ? (
          <p className="text-body-sm text-text-muted" data-testid="provider-import-no-interfaces">
            This bundle configures no interfaces, so the provider it installs collects nothing.
          </p>
        ) : (
          <ul className="flex flex-col gap-1" data-testid="provider-import-interfaces">
            {preview.interfaces.map((iface) => (
              <li key={iface.name} className="border-b border-border py-1 last:border-b-0">
                <div className="flex flex-wrap items-baseline gap-2 text-body-sm">
                  <span className="font-mono text-text-primary">{iface.name}</span>
                  <span className="text-meta text-text-secondary">{iface.typeId}</span>
                  {iface.trustMode !== null && (
                    <span
                      data-testid={`provider-import-trust-${iface.name}`}
                      className={cn(
                        "text-meta",
                        iface.trustMode === "tunnel" ? "text-warning" : "text-text-secondary",
                      )}
                    >
                      · {iface.trustMode}
                    </span>
                  )}
                  {iface.transportSecurity !== null && (
                    <span className="text-meta text-text-secondary">
                      · {iface.transportSecurity}
                    </span>
                  )}
                  {iface.conversionMode !== null && (
                    <span className="text-meta text-text-secondary">· {iface.conversionMode}</span>
                  )}
                  {iface.hasUnresolved && (
                    <span className="text-meta text-destructive">— unresolved placeholder</span>
                  )}
                </div>
                {iface.connections.map((connection) => (
                  <div
                    key={connection.name}
                    data-testid={`provider-import-connection-${connection.name}`}
                    className="pl-4 text-meta text-text-secondary"
                  >
                    ↳ <span className="font-mono">{connection.name}</span>{" "}
                    {connection.role === "dial_out" && connection.endpointHost !== null ? (
                      <span
                        className={cn("font-mono", connection.hasUnresolved && "text-destructive")}
                      >
                        → {connection.endpointHost}
                        {connection.endpointPort !== null ? `:${connection.endpointPort}` : ""}
                      </span>
                    ) : (
                      <span>
                        {connection.role === "listen" ? "Meridian listens" : "Meridian dials out"}
                        {connection.endpointPort !== null ? ` · :${connection.endpointPort}` : ""}
                      </span>
                    )}
                    {/* Marked on the ROW that carries it, not only in the summary
                        above: an operator fixing one needs to see which
                        connection it belongs to. */}
                    {connection.hasUnresolved && (
                      <span className="text-destructive"> — unresolved placeholder</span>
                    )}
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="pb-1 text-body-sm font-medium text-text-primary">Artifacts</h3>
        <ul className="flex flex-col gap-1" data-testid="provider-import-artifacts">
          {preview.artifacts.map((artifact) => (
            <li key={artifact.member} className="text-meta">
              <span className="font-mono text-text-primary">{artifact.member}</span>{" "}
              <span className="text-text-secondary">{artifact.kind}</span>
              <div
                className={cn(
                  "break-all font-mono",
                  artifact.actualSha256 === artifact.declaredSha256
                    ? "text-text-muted"
                    : "text-destructive",
                )}
              >
                sha256:{artifact.declaredSha256}
              </div>
            </li>
          ))}
        </ul>
        <p className="pt-1 text-meta text-text-muted">
          Each digest is what the installer verifies the member against, so an archive altered in
          transit is refused rather than installed.
        </p>
      </section>

      <p className="text-meta text-text-muted" data-testid="provider-import-apply-once">
        Importing is deliberate, and it is the only act that re-applies a bundle. A provider this
        deployment deleted is re-created; a provider whose bundle has changed is replaced — its
        configuration becomes what the bundle states, and interfaces the bundle no longer carries
        are removed; an unchanged bundle for a provider still here changes nothing. Restarts and
        upgrades never re-apply a bundle on their own.
      </p>
    </div>
  );
}

/** The installer's verdict, with the four outcomes kept apart. */
function OutcomeBody({ outcome }: { outcome: ProviderImportOutcome }): React.ReactElement {
  // `replaced` is a subset of `applied`; what is left is a genuine install (new, or
  // re-created after a delete — the installer does not distinguish those, and to the
  // operator both are "it is here now").
  const installed = outcome.applied.filter((name) => !outcome.replaced.includes(name));
  return (
    <div className="flex flex-col gap-2 text-body-sm" data-testid="provider-import-outcome">
      {installed.length > 0 && (
        <p className="text-text-primary" data-testid="provider-import-applied">
          Installed <span className="font-mono">{installed.join(", ")}</span>.
        </p>
      )}
      {outcome.replaced.length > 0 && (
        <p className="text-text-primary" data-testid="provider-import-replaced">
          Replaced <span className="font-mono">{outcome.replaced.join(", ")}</span> — the provider's
          configuration is now what this bundle states. Its listeners re-bind to the new
          configuration.
        </p>
      )}
      {outcome.already_applied.length > 0 && (
        <p className="text-text-secondary" data-testid="provider-import-already-applied">
          <span className="font-mono">{outcome.already_applied.join(", ")}</span> was already
          applied to this deployment and its provider is still installed as the bundle states.
          Nothing changed.
        </p>
      )}
      {outcome.rejected.length > 0 && (
        <div role="alert" data-testid="provider-import-rejected" className="text-destructive">
          <p className="pb-1">Refused, and nothing was written:</p>
          <ul className="flex flex-col gap-0.5">
            {outcome.rejected.map((rejection) => (
              <li key={rejection.bundle}>
                <span className="font-mono">{rejection.bundle}</span> — {rejection.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {outcome.applied.length === 0 &&
        outcome.already_applied.length === 0 &&
        outcome.rejected.length === 0 && (
          <p className="text-text-secondary" data-testid="provider-import-nothing">
            The deployment applied nothing from this bundle and reported no reason. Nothing changed.
          </p>
        )}
    </div>
  );
}

/**
 * Map an import failure to operator-facing copy.
 *
 * The BFF's typed refusals carry a `message` written for this surface, so it is
 * shown verbatim where present; everything else is generic (REQ-UI-037).
 */
function describeImportError(status: number, body: unknown): string {
  if (status === 422 && typeof body === "object" && body !== null) {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "object" && detail !== null) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string") return `The deployment refused this bundle: ${message}`;
    }
  }
  if (status === 403) return "This session may not import providers.";
  return "Could not import this bundle. Nothing was installed.";
}
